import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { observer, inject } from 'mobx-react';
import Product from '@/components/products/Product';
import ArrowTwoIcon from '@/components/svg/ArrowTwoIcon';

const MyFavorites = inject('studioStore', 'wooCommerceStore', 'favoritesStore')(observer(({
  studioStore,
  wooCommerceStore,
  favoritesStore,
}) => {
  const t = useTranslations('Account');
  const [openCategories, setOpenCategories] = useState({});
  const [categorizedProducts, setCategorizedProducts] = useState({});

  useEffect(() => {
    // This call is now deduplicated - safe to call multiple times
    favoritesStore.loadFavorites();
  }, [favoritesStore]);

  // Group products by top-level category
  useEffect(() => {
    if (Array.isArray(favoritesStore.favorites) && favoritesStore.favorites.length > 0) {
      const groupedProducts = {};

      favoritesStore.favorites.forEach(product => {
        // Find top-level category (parent=0)
        const topCategory = product.categories?.[0] || {
          id: 0,
          name: t('uncategorized'),
        };

        if (!groupedProducts[topCategory.id]) {
          groupedProducts[topCategory.id] = {
            name: topCategory.name,
            products: [],
          };
        }

        groupedProducts[topCategory.id].products.push(product);
      });

      setCategorizedProducts(groupedProducts);

      // Initialize all categories as open if there are few categories
      if (Object.keys(groupedProducts).length <= 2) {
        const initialOpenState = {};
        Object.keys(groupedProducts).forEach(catId => {
          initialOpenState[catId] = true;
        });
        setOpenCategories(initialOpenState);
      }
    }
  }, [favoritesStore.favorites, t]);

  const toggleCategory = (categoryId) => {
    setOpenCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  if (favoritesStore.isLoading) {
    return <div className="p-4">{t('loading')}...</div>;
  }

  if (favoritesStore.error) {
    return <div className="p-4 text-red-500">{favoritesStore.error}</div>;
  }

  // Ensure favorites is an array
  const favorites = Array.isArray(favoritesStore.favorites) ? favoritesStore.favorites : [];

  if (favorites.length === 0) {
    return <div className="p-4">{t('no_favorites')}</div>;
  }

  return (
    <div className="favorites-container p-3">
      <h2 className="mb-4 text-2xl font-bold text-primary-studio font-secondary">
        {t('my_favorites')}
      </h2>
      <hr className="dotted-spaced my-4" />

      {Object.entries(categorizedProducts).map(([categoryId, category]) => (
        <div key={categoryId} className="mb-4 border border-color-primary-light-studio rounded-md overflow-hidden">
          <button
            className="w-full flex justify-between items-center p-4 bg-primary-light-studio bg-primary-light-studio-hover text-primary-studio transition-colors text-left"
            onClick={() => toggleCategory(categoryId)}
          >
            <h3 className="text-lg font-medium">{category.name}</h3>
            <ArrowTwoIcon
              direction={openCategories[categoryId] ? "top" : "bottom"}
              size="20px"
              className="text-gray-500"
            />
          </button>

          {openCategories[categoryId] && (
            <div className="p-4 md:grid md:grid-cols-[repeat(auto-fill,minmax(210px,1fr))] lg:gap-7 md:gap-4 products-background">
              {category.products.map(product => (
                <Product
                  key={product?.id}
                  product={product}
                  wooCommerceStore={wooCommerceStore}
                  studioStore={studioStore}
                  forceCard
                  isFavoriteView
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}));

export default MyFavorites;